import React from 'react';
import { bool, node } from 'prop-types';
import { compose } from 'redux';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldSelect,
  FieldCheckboxGroup,
  FieldCheckbox,
  NamedLink,
} from '../../../components';
import css from './SignupForm.module.css';
import { yesNoOptions } from '../../../util/general';
import PhoneInput, { formatPhoneNumberIntl, isValidPhoneNumber } from 'react-phone-number-input';
import '../../../styles/react-phone.css';
import { userTypeOptions, supplierUserTypeOptions, customerUserTypeOptions } from '../../../util/enums';

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        form,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        values,
        isSupplierSignup,
        isCustomerSignup,
      } = fieldRenderProps;

      const classes = classNames(rootClassName || css.root, className);
      const submitDisabled = invalid || inProgress;

      const emailRequired = validators.required(
        intl.formatMessage({ id: 'SignupForm.emailRequired' })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({ id: 'SignupForm.emailInvalid' })
      );

      const passwordValidators = validators.composeValidators(
        validators.requiredStringNoTrim(intl.formatMessage({ id: 'SignupForm.passwordRequired' })),
        validators.minLength(
          intl.formatMessage(
            { id: 'SignupForm.passwordTooShort' },
            { minLength: validators.PASSWORD_MIN_LENGTH }
          ),
          validators.PASSWORD_MIN_LENGTH
        ),
        validators.maxLength(
          intl.formatMessage(
            { id: 'SignupForm.passwordTooLong' },
            { maxLength: validators.PASSWORD_MAX_LENGTH }
          ),
          validators.PASSWORD_MAX_LENGTH
        )
      );

      // Phone number
      const phoneNumberLabel = intl.formatMessage({
        id: 'SignupForm.phoneNumberLabel',
      });
      const phoneNumberPlaceholder = intl.formatMessage({
        id: 'SignupForm.phoneNumberPlaceholder',
      });

      const renderNameFields = () => (
        <div className={css.name}>
          <FieldTextInput
            className={css.firstNameRoot}
            type="text"
            id={formId ? `${formId}.fname` : 'fname'}
            name="fname"
            autoComplete="given-name"
            label={intl.formatMessage({ id: 'SignupForm.firstNameLabel' })}
            placeholder={intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder' })}
            validate={validators.required(
              intl.formatMessage({ id: 'SignupForm.firstNameRequired' })
            )}
          />
          <FieldTextInput
            className={css.lastNameRoot}
            type="text"
            id={formId ? `${formId}.lname` : 'lname'}
            name="lname"
            autoComplete="family-name"
            label={intl.formatMessage({ id: 'SignupForm.lastNameLabel' })}
            placeholder={intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder' })}
            validate={validators.required(
              intl.formatMessage({ id: 'SignupForm.lastNameRequired' })
            )}
          />
        </div>
      );

      const { phoneNumber, invalidPhoneValue } = values || {};

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldTextInput
            type="email"
            id={formId ? `${formId}.email` : 'email'}
            name="email"
            autoComplete="email"
            label={intl.formatMessage({ id: 'SignupForm.emailLabel' })}
            placeholder={intl.formatMessage({ id: 'SignupForm.emailPlaceholder' })}
            validate={validators.composeValidators(emailRequired, emailValid)}
          />
          <FieldSelect
            id={formId ? `${formId}.userType` : 'userType'}
            name="userType"
            className={css.field}
            label={intl.formatMessage({ id: 'SignupForm.userTypeLabel' })}
            validate={validators.required(intl.formatMessage({ id: 'SignupForm.requiredField' }))}
          >
            {(isSupplierSignup ? supplierUserTypeOptions : isCustomerSignup ? customerUserTypeOptions : userTypeOptions).map(({ value, labelId, disabled, hidden }) => (
              <option
                key={value || 'placeholder'}
                value={value}
                disabled={disabled || false}
                hidden={hidden || false}
              >
                {intl.formatMessage({ id: labelId })}
              </option>
            ))}
          </FieldSelect>

          <div className={css.phoneNumberWrapper}>
            <label className={css.phoneNumberLabel}>{phoneNumberLabel}</label>
            <Field name="phoneNumber">
              {({ input }) => (
                <div>
                  <PhoneInput
                    {...input}
                    format={formatPhoneNumberIntl}
                    className={css.fieldTextInput}
                    placeholder={phoneNumberPlaceholder}
                    defaultCountry="GB"
                    onChange={value => {
                      input.onChange(value);
                      const formattedNumber = formatPhoneNumberIntl(value);
                      if (isValidPhoneNumber(formattedNumber)) {
                        form.change('invalidPhoneValue', false);
                      } else {
                        form.change('invalidPhoneValue', true);
                      }
                    }}
                  />
                </div>
              )}
            </Field>
          </div>
          {invalidPhoneValue && phoneNumber && (
            <p className={css.invalidPhoneValue}>
              <FormattedMessage id="SignupForm.invalidPhoneNumber" />
            </p>
          )}
          <>
            {renderNameFields()}
            <FieldTextInput
              className={css.password}
              type="password"
              id={formId ? `${formId}.password` : 'password'}
              name="password"
              autoComplete="new-password"
              label={intl.formatMessage({ id: 'SignupForm.passwordLabel' })}
              placeholder={intl.formatMessage({ id: 'SignupForm.passwordPlaceholder' })}
              validate={passwordValidators}
            />
          </>

          {/* OLD: Design update alerts checkbox - replaced with contact consent checkbox
          <FieldCheckboxGroup
            className={css.designAlertsField}
            name="subscribedToDesignAlerts"
            id="subscribedToDesignAlerts"
            label={intl.formatMessage({ id: 'SignupForm.subscribeToDesignUpdates' })}
            options={yesNoOptions}
            type="radio"
          />
          */}

          {/* NEW: Contact consent checkbox for GDPR compliance */}
          <FieldCheckboxGroup
            className={css.consentCheckboxGroup}
            name="contactConsentCheckbox"
            id="contactConsentCheckbox"
            optionLabelClassName={css.consentLabel}
            options={[
              {
                key: 'consentGiven',
                label: intl.formatMessage({ id: 'SignupForm.contactConsentCheckbox' }),
              },
            ]}
            validate={
              isCustomerSignup
                ? validators.requiredFieldArrayCheckbox(
                    intl.formatMessage({ id: 'SignupForm.contactConsentRequired' })
                  )
                : undefined
            }
          />

          {termsAndConditions}

          <div className={css.bottomWrapper}>
            <PrimaryButton type="submit" inProgress={inProgress} disabled={submitDisabled}>
              <FormattedMessage id="SignupForm.signUp" />
            </PrimaryButton>
            <div className={css.loginText}>
              <FormattedMessage id="SignupForm.alreadyHaveAccount" />{' '}
              <NamedLink name="LoginPage">
                <FormattedMessage id="SignupForm.loginLink" />
              </NamedLink>
            </div>
          </div>
        </Form>
      );
    }}
  />
);

SignupFormComponent.defaultProps = { inProgress: false };

SignupFormComponent.propTypes = {
  inProgress: bool,
  termsAndConditions: node.isRequired,
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
